// src/utils/vitalsForm.js
//
// Pure logic for components/VitalsIntakeForm.jsx: validation against the
// ranges in docs/visit-workflow-contract.md (BE-2 rejects values outside them
// with a 400, so catching them here saves the round trip), BMI, the inline BP
// hint and the request payload. Bounds are inclusive.

export const VITAL_FIELDS = [
  { key: 'systolic',     label: 'Systolic BP',  unit: 'mmHg', min: 50, max: 260 },
  { key: 'diastolic',    label: 'Diastolic BP', unit: 'mmHg', min: 30, max: 160 },
  { key: 'pulse',        label: 'Pulse',        unit: 'bpm',  min: 20, max: 250 },
  { key: 'spo2',         label: 'SpO₂',         unit: '%',    min: 50, max: 100 },
  { key: 'temperatureC', label: 'Temperature',  unit: '°C',   min: 30, max: 45 },
  { key: 'weightKg',     label: 'Weight',       unit: 'kg',   min: 1,  max: 400 },
  { key: 'heightCm',     label: 'Height',       unit: 'cm',   min: 30, max: 250 },
];

export const EMPTY_VITALS = {
  systolic: '', diastolic: '', pulse: '', spo2: '', temperatureC: '', weightKg: '', heightCm: '',
};

const isBlank = (v) => v == null || String(v).trim() === '';

// Number or null (blank / not a finite number).
export function toNumber(v) {
  if (isBlank(v)) return null;
  const n = Number(String(v).trim());
  return Number.isFinite(n) ? n : null;
}

// -> { [fieldKey]: message }. Empty object means valid. Blank fields are fine
// individually; "at least one value" is a separate check (hasAnyVital) because
// a visit can be saved with only a chief complaint.
export function validateVitals(values) {
  const errors = {};
  VITAL_FIELDS.forEach(({ key, label, unit, min, max }) => {
    const raw = values?.[key];
    if (isBlank(raw)) return;
    const n = toNumber(raw);
    if (n == null) errors[key] = `${label}: enter a number`;
    else if (n < min || n > max) errors[key] = `${label} must be between ${min} and ${max} ${unit}`;
  });
  const hasSbp = !isBlank(values?.systolic);
  const hasDbp = !isBlank(values?.diastolic);
  if (hasSbp && !hasDbp && !errors.systolic) errors.diastolic = 'Enter diastolic BP too — systolic and diastolic go together';
  if (hasDbp && !hasSbp && !errors.diastolic) errors.systolic = 'Enter systolic BP too — systolic and diastolic go together';
  if (hasSbp && hasDbp && !errors.systolic && !errors.diastolic
      && toNumber(values.systolic) <= toNumber(values.diastolic)) {
    errors.systolic = 'Systolic should be higher than diastolic';
  }
  return errors;
}

export const hasAnyVital = (values) => VITAL_FIELDS.some(({ key }) => !isBlank(values?.[key]));

// kg / m², rounded to 1 dp (same as the server's computed bmi). null unless
// both are present and positive.
export function computeBmi(weightKg, heightCm) {
  const w = toNumber(weightKg); const h = toNumber(heightCm);
  if (w == null || h == null || w <= 0 || h <= 0) return null;
  const m = h / 100;
  return Math.round((w / (m * m)) * 10) / 10;
}

// Informational only — mirrors the server flags (BP_HIGH >=140 or >=90,
// BP_LOW <90 or <60). Returns 'high' | 'low' | null.
export function bpHint(systolic, diastolic) {
  const s = toNumber(systolic); const d = toNumber(diastolic);
  if (s == null || d == null) return null;
  if (s >= 140 || d >= 90) return 'high';
  if (s < 90 || d < 60) return 'low';
  return null;
}

// Body for POST /clinic/patients/{id}/vitals — only filled fields, as numbers.
export function toVitalsPayload(values, { appointmentId } = {}) {
  const body = {};
  if (appointmentId) body.appointmentId = appointmentId;
  VITAL_FIELDS.forEach(({ key }) => {
    const n = toNumber(values?.[key]);
    if (n != null) body[key] = n;
  });
  return body;
}

// Body for PUT .../intake — trimmed, blank fields omitted; null when both blank.
export function toIntakePayload({ chiefComplaint, currentMedicines } = {}) {
  const body = {};
  if (!isBlank(chiefComplaint)) body.chiefComplaint = chiefComplaint.trim();
  if (!isBlank(currentMedicines)) body.currentMedicines = currentMedicines.trim();
  return Object.keys(body).length ? body : null;
}
